import type { ComponentChildren } from 'preact'
import { useEffect, useId, useRef } from 'preact/hooks'
import { useFocusTrap } from '~/hooks/useFocusTrap'
import { useScrollLock } from '~/hooks/useScrollLock'
import { Button } from './Button'
import styles from './InfoSheet.module.css'

/**
 * A bottom sheet carrying an explanation or a reversible decision (D-104 §2).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  DISMISSING A SHEET NEVER PERFORMS AN ACTION
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Escape, the Close button and a backdrop tap all do the same thing: close it,
 * and nothing else. A sheet that saved on dismissal would make a mis-tap on the
 * scrim into a decision, which is the whole reason D-104 §3 keeps destructive
 * confirmations inline and anchored instead.
 *
 * `busy` disables all three paths while an operation is running, so a sheet
 * cannot be closed out from under a write that is in flight.
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS MAY NOT BE RENDERED INSIDE A GLASS SURFACE
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The sheet is `position: fixed`, and **`backdrop-filter` on an ancestor makes
 * that ancestor the containing block for fixed descendants** — so a sheet
 * rendered inside the tab bar, the decision panel or the camera countdown would
 * be positioned against the inside of that element rather than against the
 * viewport, and would be clipped by it.
 *
 * There is no portal to escape with: `createPortal` lives in `preact/compat`,
 * which is not in the pinned stack (D-025), and adding it for this would be a
 * stack change needing its own record. **`check-boundaries.mjs` therefore
 * forbids the three glass files from importing this component** — a sheet
 * belonging to content inside one is rendered by an ancestor outside it.
 *
 * The rule exists because the failure is invisible in a unit test and subtle in
 * a browser: the sheet appears, in the wrong place, only on the screens that use
 * glass.
 */
interface InfoSheetProps {
  open: boolean
  /** Announced as the sheet's accessible name. Required — a sheet says what it is. */
  title: string
  onClose: () => void
  /** Blocks every dismissal path while a write is in flight. */
  busy?: boolean
  /**
   * The control to give focus back to when this closes.
   *
   * Supplied rather than guessed: **tapping a button does not focus it in iOS
   * Safari**, so reading `document.activeElement` at open time captures `<body>`
   * on the one platform this app is built for, and a VoiceOver user is returned
   * to the document root. A component test caught it; the device would have too,
   * eventually, and later.
   */
  returnFocusTo?: { current: HTMLElement | null }
  children: ComponentChildren
}

export function InfoSheet({
  open,
  title,
  onClose,
  busy = false,
  returnFocusTo,
  children,
}: InfoSheetProps) {
  const titleId = useId()
  const sheetRef = useRef<HTMLDivElement | null>(null)
  const headingRef = useRef<HTMLHeadingElement | null>(null)

  useScrollLock(open)
  useFocusTrap(open, sheetRef, returnFocusTo === undefined ? {} : { returnTo: returnFocusTo })

  /* Focus the title, not the first control: a screen reader then announces what
     opened rather than announcing a button and leaving the user to work out
     where they are. Same reasoning as photo capture, which
     `e2e/accessibility.spec.ts` already guards. */
  useEffect(() => {
    if (!open) return
    headingRef.current?.focus()
  }, [open])

  useEffect(() => {
    if (!open) return

    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape' || busy) return
      event.stopPropagation()
      onClose()
    }

    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, busy, onClose])

  if (!open) return null

  return (
    <div class={styles.layer}>
      {/*
        A button rather than a div with a click handler. The scrim is a real
        dismissal control, so it is one a keyboard and a screen reader can find —
        and its accessible name says what it does rather than "background".
      */}
      <button
        type="button"
        class={styles.scrim}
        aria-label={`Close ${title}`}
        disabled={busy}
        onClick={() => {
          if (!busy) onClose()
        }}
      />

      <div
        class={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={sheetRef}
      >
        <div class={styles.header}>
          <h2 id={titleId} class={styles.title} tabIndex={-1} ref={headingRef}>
            {title}
          </h2>
        </div>

        <div class={styles.body}>{children}</div>

        <div class={styles.actions}>
          <Button variant="secondary" onClick={onClose} disabled={busy} fullWidth>
            Close
          </Button>
        </div>
      </div>
    </div>
  )
}
